import { useMemo } from 'react';

import { Box, Flex, Grid } from '@chakra-ui/react';
import { components } from 'react-select';
import { useReviewCreationV1Data, REVIEW_PROVIDER_TYPE_LABELS_V1 } from 'reducers/reviewCreationV1Slice';
import { useGetTemplateList } from 'screens/admin/Reviews/ReviewProcessV1/Hooks/dataHooks/elementsDataHooks';

import { PlusNotOutlined } from 'assets/assetsV1/iconComponents';
import { DottedLineManagerBold } from 'assets/assetsV1/iconComponents/DottedLineManagerBold';
import { ExternalReviewersBold } from 'assets/assetsV1/iconComponents/ExternalReviewersBold';
import { ManagerScoreBold } from 'assets/assetsV1/iconComponents/ManagerScoreBold';
import { PeerBold } from 'assets/assetsV1/iconComponents/PeerBold';
import { SelfScoreBold } from 'assets/assetsV1/iconComponents/SelfScoreBold';
import { SubordinateBold } from 'assets/assetsV1/iconComponents/SubordinateBold';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { getReviewSettingsTemplateRoute } from 'constants/helper';
import { LogoWithoutNameImage } from 'constants/icons';

import { Button } from 'uiToolkitV1/Button';
import { GradientBGCard } from 'uiToolkitV1/GradientBGCard/GradientBGCard';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextLabel } from 'uiToolkitV1/Typography';

import { useUpdateFormTemplates } from '../../dataHooks/formsSectionHooks';

const CardsGridWrapper = ({ children }) => (
  <Grid
    gridTemplateColumns="1fr 1fr"
    gridGap={4}
    // this is for positioning in the parent grid, as this will be a grid inside grid
    gridColumn="2 / span 2"
  >
    {children}
  </Grid>
);

export const OptionComp = (props) => {
  const { Option } = components;
  const { data } = props;
  return (
    <Option {...props}>
      <Flex sx={{ color: '#333' }}>
        {data.label}
        {data.templateData.isSample && (
          <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginLeft: '12px' }} />
        )}
      </Flex>
    </Option>
  );
};

export const SingleValueComp = (props) => {
  const { SingleValue } = components;
  const { data } = props;
  return (
    <SingleValue {...props}>
      <Flex sx={{ color: '#333' }}>
        {data.label}
        {data.templateData.isSample && (
          <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginLeft: '12px' }} />
        )}
      </Flex>
    </SingleValue>
  );
};

const typeToIconMap = {
  [REVIEW_PROVIDER_TYPES.SELF]: SelfScoreBold,
  [REVIEW_PROVIDER_TYPES.MANAGER]: ManagerScoreBold,
  [REVIEW_PROVIDER_TYPES.PEER]: PeerBold,
  [REVIEW_PROVIDER_TYPES.UPWARD]: SubordinateBold,
  [REVIEW_PROVIDER_TYPES.DLM]: DottedLineManagerBold,
  [REVIEW_PROVIDER_TYPES.EXTERNAL]: ExternalReviewersBold,
};

const IconLabelComp = ({ providerType, tooltipTitle = '' }) => {
  const Icon = typeToIconMap[providerType];
  return (
    <Flex alignItems="center" gap={2} bg="white" px={2} py={1} borderRadius="16px">
      <Icon color="text.g2" boxSize="24px" />
      {providerType === REVIEW_PROVIDER_TYPES.EXTERNAL ? (
        <TextLabel bold flexGrow={1}>
          External peer review
        </TextLabel>
      ) : (
        <TextLabel bold flexGrow={1}>
          {REVIEW_PROVIDER_TYPE_LABELS_V1[providerType]} review
        </TextLabel>
      )}
      {tooltipTitle && <TooltipInfoV1 title={tooltipTitle} />}
    </Flex>
  );
};

export const FormsCards = () => {
  const { enabledProviderTypes, providerTypeToTemplateMap } = useReviewCreationV1Data();
  const { data: templates = [], isLoading: isLoadingTemplateList } = useGetTemplateList();
  const { mutate: updateFormsTemplates } = useUpdateFormTemplates();

  const templateSelectOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: template.id,
        templateData: { templateId: template.id, isSample: template.isSample, templateName: template.name },
      })),
    [templates]
  );

  const onChangeHandler = ({ data, providerType }) => {
    const newTemplateProviderTypeObj = { ...providerTypeToTemplateMap };
    newTemplateProviderTypeObj[providerType] = data.templateData;

    updateFormsTemplates({
      providerTypeToTemplateMap: {
        [providerType]: { templateId: data.templateData?.templateId },
      },
    });
  };

  return (
    <CardsGridWrapper>
      {enabledProviderTypes.map((providerType) => {
        const template = providerTypeToTemplateMap[providerType];
        return (
          <GradientBGCard key={providerType}>
            <Flex justifyContent="center" gap={3} alignItems="center" mb={2}>
              <Box flexGrow={1}>
                <IconLabelComp
                  providerType={providerType}
                  tooltipTitle={
                    providerType !== REVIEW_PROVIDER_TYPES.PEER
                      ? ''
                      : 'Peers need to be nominated. Access for employees and managers to nominate peers is enabled. Auto-accept nomination request is enabled. Click configure to change settings.'
                  }
                />
              </Box>
              <Button
                StartIcon={PlusNotOutlined}
                variant="outlined"
                p="0 6px 0 0"
                height="32px"
                iconSize="24px"
                as="a"
                target="_blank"
                borderRadius="8px"
                href={`${getReviewSettingsTemplateRoute()}&isCreateTemplatePanelOpen=true`}
              >
                Create Template
              </Button>
            </Flex>

            <SelectDashed
              value={templateSelectOptions.find((o) => o.value === template?.templateId) ?? null}
              placeholder="Select a template"
              options={templateSelectOptions}
              handleChange={(data) => {
                onChangeHandler({ data, providerType });
              }}
              minWidth="320px"
              isDisabled={isLoadingTemplateList}
              components={{ Option: OptionComp, SingleValue: SingleValueComp }}
            />
          </GradientBGCard>
        );
      })}
    </CardsGridWrapper>
  );
};
